import cn from 'classnames';
import { isValidElement, useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from '@orionprotocol/utils';
import * as Tooltip from '@radix-ui/react-tooltip';

import { useIsMobile } from '../utils';

import styles from './WithTooltip.module.css';

type WithTooltipProps = {
  active?: boolean;
  container?: HTMLElement | null;
  tooltipClassName?: string;
  arrowClassName?: string;
  tooltip: JSX.Element | string;
  width?: number;
  mobileWidth?: number;
} & Tooltip.PopperContentProps;

export function WithTooltip({
  children,
  tooltip,
  active = true,
  container,
  side,
  tooltipClassName,
  arrowClassName,
  width,
  mobileWidth,
}: WithTooltipProps): JSX.Element | null {
  const triggerContainer = useRef(null);
  const isMobile = useIsMobile();
  const maxWidth = isMobile && mobileWidth !== undefined ? mobileWidth : width;
  const [mobileOpen, setMobileOpen] = useState<boolean>();

  useOnClickOutside(triggerContainer, () => setMobileOpen(false));

  useEffect(() => {
    if (container) {
      if (window.getComputedStyle(container).position === 'static') {
        console.warn(`Tooltip container must have non static position`);
      }
    }
  }, [container]);

  if (!active && isValidElement(children)) return children;

  return isValidElement(children) ? (
    <Tooltip.Provider delayDuration={250}>
      <Tooltip.Root open={isMobile ? mobileOpen : undefined}>
        <Tooltip.Trigger
          asChild
          onTouchStart={(): void => {
            setMobileOpen(true);
          }}
          ref={triggerContainer}
        >
          {children}
        </Tooltip.Trigger>
        <Tooltip.Portal container={container ?? triggerContainer.current}>
          <Tooltip.Content
            className={cn(tooltipClassName, styles.tooltipContent)}
            side={side}
            sideOffset={5}
            style={{
              maxWidth,
            }}
          >
            {tooltip}
            <Tooltip.Arrow
              className={cn(arrowClassName, styles.tooltipArrow)}
            />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  ) : null;
}
